import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useAnalysis } from '../contexts/AnalysisContext';
import { PfmeaItem } from '../types';
import {
    FileSpreadsheet,
    Plus,
    Filter,
    Download,
    ChevronDown,
    ChevronUp,
    Search,
    X,
    Edit3,
    Eye,
    Trash2,
    Printer,
    RefreshCw,
    ClipboardCheck,
    Link2,
    Settings2,
    WrapText,
    AlignLeft,
} from 'lucide-react';
import { NextStepButton } from '../components/WorkflowNavigation';
import { PfmeaLayoutSettingsPanel, PfmeaLayoutSettings, DEFAULT_PFMEA_LAYOUT_SETTINGS } from '../components/PfmeaEditor/LayoutSettingsPanel';
import { PfmeaCellRenderer } from '../components/PfmeaEditor/PfmeaCellRenderer';

const PrintablePfmeaTable: React.FC<{ items: PfmeaItem[], standardName: string }> = ({ items, standardName }) => {
    return (
        <div className="hidden print:block font-sans text-black bg-white p-4 print:text-[10px]">
            <div className="mb-4 pb-2 border-b-2 border-black flex justify-between items-center">
                <div>
                    <h1 className="text-xl font-bold uppercase tracking-tight">PFMEA Worksheet</h1>
                    <div className="text-sm mt-1 text-gray-600">Standard: <span className="font-semibold text-black">{standardName}</span></div>
                </div>
                <div className="text-right text-xs">
                    <div>Generated: <span className="font-semibold">{new Date().toLocaleDateString()}</span></div>
                    <div>Total Items: <span className="font-semibold">{items.length}</span></div>
                </div>
            </div>

            <table className="w-full text-left border-collapse table-fixed">
                <thead>
                    <tr className="border-b-2 border-black bg-gray-50 text-[9px]">
                        <th className="py-1 px-1 border-r border-gray-300 w-8">Step</th>
                        <th className="py-1 px-1 border-r border-gray-300 w-24">Function</th>
                        <th className="py-1 px-1 border-r border-gray-300 w-24">Failure Mode</th>
                        <th className="py-1 px-1 border-r border-gray-300 w-24">Effect</th>
                        <th className="py-1 px-1 border-r border-gray-300 w-8">S</th>
                        <th className="py-1 px-1 border-r border-gray-300 w-24">Cause</th>
                        <th className="py-1 px-1 border-r border-gray-300 w-8">O</th>
                        <th className="py-1 px-1 border-r border-gray-300 w-24">Controls (Prev/Det)</th>
                        <th className="py-1 px-1 border-r border-gray-300 w-8">D</th>
                        <th className="py-1 px-1 border-r border-gray-300 w-8">RPN</th>
                        <th className="py-1 px-1 w-8">AP</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, idx) => {
                        const rpn = item.severity * item.occurrence * item.detection;
                        return (
                            <tr key={idx} className="border-b border-gray-200 break-inside-avoid">
                                <td className="py-1 px-1 align-top border-r border-gray-300 font-mono">{item.processStepNumber}</td>
                                <td className="py-1 px-1 align-top border-r border-gray-300">{item.processFunction}</td>
                                <td className="py-1 px-1 align-top border-r border-gray-300 font-semibold">{item.failureMode}</td>
                                <td className="py-1 px-1 align-top border-r border-gray-300">{item.failureEffectEnd}</td>
                                <td className="py-1 px-1 align-top border-r border-gray-300 text-center font-bold">{item.severity}</td>
                                <td className="py-1 px-1 align-top border-r border-gray-300">{item.failureCause}</td>
                                <td className="py-1 px-1 align-top border-r border-gray-300 text-center font-bold">{item.occurrence}</td>
                                <td className="py-1 px-1 align-top border-r border-gray-300">
                                    <div className="text-[8px] text-gray-500">P: {item.preventionControls?.join(', ') || '-'}</div>
                                    <div className="text-[8px] text-gray-500 mt-1">D: {item.detectionControls?.join(', ') || '-'}</div>
                                </td>
                                <td className="py-1 px-1 align-top border-r border-gray-300 text-center font-bold">{item.detection}</td>
                                <td className="py-1 px-1 align-top border-r border-gray-300 text-center font-bold">{rpn}</td>
                                <td className="py-1 px-1 align-top text-center font-bold">{item.actionPriority}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <div className="mt-4 pt-2 border-t border-gray-300 text-[9px] text-gray-500 flex justify-between">
                <span>Magnus PFMEA System</span>
                <span>Confidential - For Internal Use Only</span>
            </div>
        </div>
    );
};

const PfmeaTablePage: React.FC = () => {
    const navigate = useNavigate();
    const { pfmeaItems, standardConfig, deletePfmeaItem } = useAppContext();
    const { hasPermission } = useAuth();
    const { syncToPfmea, isSyncing, failureChains } = useAnalysis();

    // Filters
    const [filterRisk, setFilterRisk] = useState<'all' | 'H' | 'M' | 'L'>('all');
    const [filter4M, setFilter4M] = useState<'all' | 'Man' | 'Machine' | 'Material' | 'Method'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [syncMessage, setSyncMessage] = useState<string | null>(null);

    // Sorting
    const [sortField, setSortField] = useState<keyof PfmeaItem>('processStepNumber');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    // Layout Settings
    const [layoutSettings, setLayoutSettings] = useState<PfmeaLayoutSettings>(DEFAULT_PFMEA_LAYOUT_SETTINGS);
    const [layoutSettingsOpen, setLayoutSettingsOpen] = useState(false);

    // Get visible columns from standard config
    const columns = standardConfig?.columns.filter(col => col.visible) || [];

    // Handler: Sync Failure Chains to PFMEA
    const handleSyncFromFailureChains = () => {
        const result = syncToPfmea();
        if (result.count > 0) {
            setSyncMessage(`Synced ${result.count} new PFMEA items from failure chains`);
        } else {
            setSyncMessage('No new items to sync - all failure chains already have PFMEA items');
        }
        setTimeout(() => setSyncMessage(null), 5000);
    };

    // Handler: Navigate to Control Plan
    const handleGenerateControlPlan = () => {
        navigate('/control-plan');
    };

    // Handler: View/Edit PFMEA item
    const handleViewItem = (itemId: string) => {
        navigate(`/pfmea-editor?id=${itemId}`);
    };

    // Handler: Delete PFMEA item
    const handleDeleteItem = (itemId: string) => {
        if (window.confirm('Are you sure you want to delete this PFMEA item?')) {
            deletePfmeaItem(itemId);
        }
    };

    // Filter and sort items
    const filteredItems = useMemo(() => {
        let items = [...pfmeaItems];

        // Apply filters
        if (filterRisk !== 'all') {
            items = items.filter(i => i.actionPriority === filterRisk);
        }
        if (filter4M !== 'all') {
            items = items.filter(i => i.processElement === filter4M);
        }
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            items = items.filter(i =>
                i.failureMode.toLowerCase().includes(term) ||
                i.processStepName.toLowerCase().includes(term) ||
                i.failureCause.toLowerCase().includes(term)
            );
        }

        // Apply sorting
        items.sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];
            if (aVal === undefined || bVal === undefined) return 0;
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return sortDirection === 'asc'
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            }
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
            }
            return 0;
        });

        return items;
    }, [pfmeaItems, filterRisk, filter4M, searchTerm, sortField, sortDirection]);

    const handleSort = (field: keyof PfmeaItem) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const SortIcon = ({ field }: { field: keyof PfmeaItem }) => {
        if (sortField !== field) return null;
        return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
    };

    // Row colour based on layoutSettings.colorMode
    const getViewerRowClass = (item: PfmeaItem, idx: number): string => {
        const { colorMode, riskRanges } = layoutSettings;
        const maxRating = Math.max(item.severity ?? 0, item.occurrence ?? 0, item.detection ?? 0);
        if (colorMode === 'risk-traffic-light') {
            if (maxRating >= riskRanges.red.min && maxRating <= riskRanges.red.max)
                return 'bg-red-50 dark:bg-red-900/10 hover:bg-red-100/50';
            if (maxRating >= riskRanges.yellow.min && maxRating <= riskRanges.yellow.max)
                return 'bg-yellow-50 dark:bg-yellow-900/10 hover:bg-yellow-100/50';
            if (maxRating >= riskRanges.green.min && maxRating <= riskRanges.green.max)
                return 'bg-green-50 dark:bg-green-900/10 hover:bg-green-100/50';
            return 'hover:bg-[var(--bg-tertiary)]';
        }
        if (colorMode === 'alt-gray')
            return idx % 2 === 0 ? 'bg-white dark:bg-slate-900 hover:bg-slate-50' : 'bg-slate-50 dark:bg-slate-800/40 hover:bg-slate-100/60';
        if (colorMode === 'alt-blue')
            return idx % 2 === 0 ? 'bg-white dark:bg-slate-900 hover:bg-blue-50/30' : 'bg-blue-50/50 dark:bg-blue-900/10 hover:bg-blue-100/40';
        if (colorMode === 'alt-green-white')
            return idx % 2 === 0 ? 'bg-white dark:bg-slate-900 hover:bg-emerald-50/30' : 'bg-emerald-50/40 dark:bg-emerald-900/10 hover:bg-emerald-100/40';
        return 'hover:bg-[var(--bg-tertiary)]';
    };


    return (
        <>
            <div className="space-y-6 print:hidden">
                {/* Sync Message Banner */}
                {syncMessage && (
                    <div className="bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 text-green-800 dark:text-green-200 px-4 py-3 rounded-lg flex items-center gap-2">
                        <Link2 size={18} />
                        {syncMessage}
                    </div>
                )}

                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[var(--text-primary)] flex items-center gap-2">
                            <FileSpreadsheet className="text-primary-500" />
                            PFMEA Worksheet
                        </h1>
                        <p className="text-[var(--text-secondary)]">
                            {filteredItems.length} of {pfmeaItems.length} items · {standardConfig?.name}
                            {failureChains.length > 0 && (
                                <span className="ml-2 text-primary-500">
                                    ({failureChains.length} failure chains available)
                                </span>
                            )}
                        </p>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={18} />
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>

                        {/* Filter Toggle */}
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 ${showFilters || filterRisk !== 'all' || filter4M !== 'all'
                                ? 'bg-primary-100 border-primary-300 text-primary-700 dark:bg-primary-900 dark:border-primary-700 dark:text-primary-300'
                                : 'bg-[var(--bg-tertiary)] border-[var(--border-color)] text-[var(--text-primary)]'
                                }`}
                        >
                            <Filter size={18} />
                            Filters
                            {(filterRisk !== 'all' || filter4M !== 'all') && (
                                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-primary-500 text-white text-xs">
                                    {(filterRisk !== 'all' ? 1 : 0) + (filter4M !== 'all' ? 1 : 0)}
                                </span>
                            )}
                        </button>

                        {/* Sync from Failure Chains - Golden Thread */}
                        {hasPermission('pfmea.edit') && failureChains.length > 0 && (
                            <button
                                onClick={handleSyncFromFailureChains}
                                disabled={isSyncing}
                                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                                title="Sync failure chains to PFMEA items"
                            >
                                <RefreshCw size={18} className={isSyncing ? 'animate-spin' : ''} />
                                Sync Chains
                            </button>
                        )}

                        {/* Generate Control Plan */}
                        {pfmeaItems.length > 0 && (
                            <button
                                onClick={handleGenerateControlPlan}
                                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                                title="Generate Control Plan from PFMEA items"
                            >
                                <ClipboardCheck size={18} />
                                Control Plan
                            </button>
                        )}

                        {/* Wrap Text Quick Toggle */}
                        <button
                            onClick={() => setLayoutSettings(prev => ({ ...prev, wrapText: !prev.wrapText }))}
                            className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 ${layoutSettings.wrapText
                                ? 'bg-blue-600 text-white border-blue-700'
                                : 'bg-[var(--bg-tertiary)] border-[var(--border-color)] text-[var(--text-primary)]'
                                }`}
                            title={layoutSettings.wrapText ? 'Wrap ON – click to truncate' : 'Wrap OFF – click to wrap'}
                        >
                            {layoutSettings.wrapText ? <><WrapText size={18} /> Wrap</> : <><AlignLeft size={18} /> No Wrap</>}
                        </button>

                        {/* Layout Settings */}
                        <button
                            onClick={() => setLayoutSettingsOpen(true)}
                            className="px-4 py-2 bg-[var(--bg-tertiary)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--bg-primary)] border border-[var(--border-color)] transition-colors flex items-center gap-2"
                            title="Layout & Display Settings"
                        >
                            <Settings2 size={18} />
                            Layout
                        </button>

                        {/* Export */}
                        <button className="px-4 py-2 bg-[var(--bg-tertiary)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--bg-primary)] border border-[var(--border-color)] transition-colors flex items-center gap-2">
                            <Download size={18} />
                            Export
                        </button>
                        <button
                            onClick={() => window.print()}
                            className="px-4 py-2 bg-[var(--bg-tertiary)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--bg-primary)] border border-[var(--border-color)] transition-colors flex items-center gap-2"
                        >
                            <Printer size={18} />
                            Print
                        </button>

                        {/* Add */}
                        {hasPermission('pfmea.create') && (
                            <button
                                onClick={() => navigate('/pfmea-editor')}
                                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
                            >
                                <Plus size={18} />
                                Add Item
                            </button>
                        )}
                    </div>
                </div>

                {/* Filters Bar */}
                {showFilters && (
                    <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-color)] p-4 flex flex-wrap gap-4 items-center">
                        {/* Action Priority Filter */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-[var(--text-secondary)]">Priority:</span>
                            <div className="flex items-center bg-[var(--bg-tertiary)] rounded-lg p-1">
                                {['all', 'H', 'M', 'L'].map((risk) => (
                                    <button
                                        key={risk}
                                        onClick={() => setFilterRisk(risk as any)}
                                        className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${filterRisk === risk
                                            ? risk === 'H' ? 'bg-red-500 text-white'
                                                : risk === 'M' ? 'bg-amber-500 text-white'
                                                    : risk === 'L' ? 'bg-green-500 text-white'
                                                        : 'bg-primary-500 text-white'
                                            : 'text-[var(--text-secondary)] hover:bg-[var(--bg-primary)]'
                                            }`}
                                    >
                                        {risk === 'all' ? 'All' : risk}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* 4M Filter */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-[var(--text-secondary)]">4M Category:</span>
                            <div className="flex items-center bg-[var(--bg-tertiary)] rounded-lg p-1">
                                {['all', 'Man', 'Machine', 'Material', 'Method'].map((cat) => (
                                    <button
                                        key={cat}
                                        onClick={() => setFilter4M(cat as any)}
                                        className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${filter4M === cat
                                            ? 'bg-primary-500 text-white'
                                            : 'text-[var(--text-secondary)] hover:bg-[var(--bg-primary)]'
                                            }`}
                                    >
                                        {cat === 'all' ? 'All' : cat}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Clear Filters */}
                        {(filterRisk !== 'all' || filter4M !== 'all') && (
                            <button
                                onClick={() => { setFilterRisk('all'); setFilter4M('all'); }}
                                className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1"
                            >
                                <X size={14} />
                                Clear filters
                            </button>
                        )}
                    </div>
                )}

                {/* Table */}
                <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-color)] overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-[var(--bg-tertiary)]">
                                    {columns.map(col => (
                                        <th
                                            key={col.key}
                                            onClick={() => handleSort(col.key as keyof PfmeaItem)}
                                            className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider cursor-pointer hover:bg-[var(--bg-primary)] select-none"
                                            style={{ minWidth: col.width }}
                                        >
                                            <div className="flex items-center gap-1">
                                                {col.label}
                                                <SortIcon field={col.key as keyof PfmeaItem} />
                                            </div>
                                        </th>
                                    ))}
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider w-24">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--border-color)]">
                                {filteredItems.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length + 1} className="px-4 py-12 text-center">
                                            <FileSpreadsheet className="mx-auto text-[var(--text-secondary)] mb-4" size={48} />
                                            <p className="text-lg font-medium text-[var(--text-primary)]">No items found</p>
                                            <p className="text-sm text-[var(--text-secondary)]">
                                                {pfmeaItems.length === 0
                                                    ? 'Start by adding PFMEA items or generate with AI.'
                                                    : 'Try adjusting your filters or search term.'
                                                }
                                            </p>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredItems.map((item, idx) => (
                                        <tr
                                            key={item.id}
                                            className={`transition-colors ${getViewerRowClass(item, idx)}`}
                                        >
                                            {columns
                                                .filter(col => !layoutSettings.hiddenColumns.has(col.key))
                                                .map(col => (
                                                    <td
                                                        key={col.key}
                                                        className={`px-4 py-3 text-sm text-[var(--text-primary)] ${layoutSettings.wrapText ? 'whitespace-pre-wrap break-words' : 'truncate max-w-[200px]'
                                                            } ${layoutSettings.showCellBorders ? 'border border-slate-200 dark:border-slate-700' : ''}`}
                                                    >
                                                        <PfmeaCellRenderer item={item} colKey={col.key} />
                                                    </td>
                                                ))
                                            }
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleViewItem(item.id)}
                                                        className="p-1 text-[var(--text-secondary)] hover:text-primary-500 transition-colors"
                                                        title="View"
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                    {hasPermission('pfmea.edit') && (
                                                        <button
                                                            onClick={() => handleViewItem(item.id)}
                                                            className="p-1 text-[var(--text-secondary)] hover:text-primary-500 transition-colors"
                                                            title="Edit"
                                                        >
                                                            <Edit3 size={16} />
                                                        </button>
                                                    )}
                                                    {hasPermission('pfmea.delete') && (
                                                        <button
                                                            onClick={() => handleDeleteItem(item.id)}
                                                            className="p-1 text-[var(--text-secondary)] hover:text-red-500 transition-colors"
                                                            title="Delete"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer */}
                    {filteredItems.length > 0 && (
                        <div className="px-4 py-3 bg-[var(--bg-tertiary)] border-t border-[var(--border-color)] flex items-center justify-between text-sm">
                            <p className="text-[var(--text-secondary)]">
                                Showing {filteredItems.length} of {pfmeaItems.length} items
                            </p>
                            <div className="flex items-center gap-4">
                                <span className="text-[var(--text-secondary)]">
                                    High: <span className="font-bold text-red-500">{filteredItems.filter(i => i.actionPriority === 'H').length}</span>
                                </span>
                                <span className="text-[var(--text-secondary)]">
                                    Medium: <span className="font-bold text-amber-500">{filteredItems.filter(i => i.actionPriority === 'M').length}</span>
                                </span>
                                <span className="text-[var(--text-secondary)]">
                                    Low: <span className="font-bold text-green-500">{filteredItems.filter(i => i.actionPriority === 'L').length}</span>
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Continue to Optimization */}
                {pfmeaItems.length > 0 && (
                    <div className="mt-6 p-4 bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-color)] flex items-center justify-between">
                        <div>
                            <h3 className="font-semibold text-[var(--text-primary)]">PFMEA analysis complete?</h3>
                            <p className="text-sm text-[var(--text-secondary)]">Proceed to track recommended actions</p>
                        </div>
                        <NextStepButton currentStep={5} isComplete={pfmeaItems.length > 0} />
                    </div>
                )}
            </div>
            <PrintablePfmeaTable items={filteredItems} standardName={standardConfig?.name || 'Unknown Standard'} />

            {/* Layout Settings Panel */}
            {layoutSettingsOpen && (
                <PfmeaLayoutSettingsPanel
                    settings={layoutSettings}
                    onSettingsChange={setLayoutSettings}
                    onClose={() => setLayoutSettingsOpen(false)}
                    availableColumns={columns.map(col => ({ key: col.key, label: col.label }))}
                />
            )}
        </>
    );
};

export default PfmeaTablePage;
